import {
  APP_TIME_ZONE,
  type LocalDate,
  type LocalDateTime,
  type LocalMonth,
  type LocalQuarter,
  addDays,
  addMonths,
  endOf,
  formatFrenchDate,
  monthOf,
  quarterOf,
  timeOf,
} from '@ergo/shared';

/**
 * Même calendrier que le serveur : l'arithmétique naïve vit dans `@ergo/shared`
 * (mêmes chaînes Europe/Paris, mêmes jours civils), et ce fichier n'en est plus que
 * la façade. Il ne garde en propre que ce qui regarde une horloge ou parle français :
 * l'heure de Paris et les formats `Intl` du navigateur.
 */
export {
  APP_TIME_ZONE,
  addDays,
  addMinutes,
  addMonths,
  atTime,
  dateOf,
  daysBetween,
  endOf as endsAt,
  firstMonthOfQuarter,
  firstOccurrence,
  formatDuration,
  formatFrenchDate as formatDate,
  formatFrenchTime,
  isValidDate,
  isValidMonth,
  isValidQuarter,
  isValidTime,
  minutesOfTime,
  monthOf,
  nextQuarter,
  previousQuarter,
  quarterOf,
  startOfWeek,
  timeFromMinutes,
  timeOf,
  weekdayOf,
} from '@ergo/shared';

export function today(now: Date = new Date()): LocalDate {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIME_ZONE }).format(now);
}

/** Current wall-clock instant as `YYYY-MM-DDTHH:MM`, comparable with `startsAt`. */
export function now(at: Date = new Date()): LocalDateTime {
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(at);
  return `${today(at)}T${time}`;
}

/** The month the report opens on: invoicing happens once the month is over. */
export function previousMonth(now: Date = new Date()): LocalMonth {
  return addMonths(monthOf(today(now)), -1);
}

export function currentQuarter(now: Date = new Date()): LocalQuarter {
  return quarterOf(today(now));
}

/** « T3 2026 » — the way she reads it on urssaf.fr. */
export function formatQuarter(quarter: LocalQuarter): string {
  return `T${quarter.slice(6, 7)} ${quarter.slice(0, 4)}`;
}

// ---------------------------------------------------------------- formatting

/** Noon avoids any rounding surprise when a formatter re-projects the date. */
function asDate(date: LocalDate): Date {
  return new Date(`${date}T12:00:00Z`);
}

function formatWith(date: LocalDate, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', ...options }).format(asDate(date));
}

/** « lundi 31/08/2026 » */
export function formatLongDate(date: LocalDate): string {
  return `${formatWith(date, { weekday: 'long' })} ${formatFrenchDate(date)}`;
}

/** « lun. 31/08 » */
export function formatDayMonth(date: LocalDate): string {
  return `${formatWith(date, { weekday: 'short' })} ${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

/** « 18 oct. » — une borne de vacances au milieu d'une phrase. */
export function formatDayMonthShort(date: LocalDate): string {
  return formatWith(date, { day: 'numeric', month: 'short' });
}

/** « 31/08 » — sur un relevé, où l'année se lit dans la colonne d'à côté. */
export function formatShortDate(date: LocalDate): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`;
}

export function formatWeekdayShort(date: LocalDate): string {
  return formatWith(date, { weekday: 'short' });
}

export function formatDayNumber(date: LocalDate): string {
  return formatWith(date, { day: 'numeric' });
}

/** « août 2026 » */
export function formatMonth(month: LocalMonth): string {
  return formatWith(`${month}-01`, { month: 'long', year: 'numeric' });
}

/** « sept. 2026 » — la version qui tient dans une pastille. */
export function formatMonthShort(month: LocalMonth): string {
  return formatWith(`${month}-01`, { month: 'short', year: 'numeric' });
}

/** « août » — le mois seul, au milieu d'une phrase : « et août est bouclé ». */
export function formatMonthLong(month: LocalMonth): string {
  return formatWith(`${month}-01`, { month: 'long' });
}

/**
 * « d'août », « de septembre » — le mois dans une phrase, avec son élision :
 * « Clôture d'août », « les chiffres de septembre ». Cinq mois commencent par une
 * voyelle, et la règle est trop courte pour mériter une table.
 */
export function deMois(month: LocalMonth): string {
  const name = formatMonthLong(month);
  return /^[aeiouâéèêîôùû]/i.test(name) ? `d’${name}` : `de ${name}`;
}

/** « sept. » — sous une cellule de trimestre, l'année est déjà écrite au-dessus. */
export function formatMonthName(month: LocalMonth): string {
  return formatWith(`${month}-01`, { month: 'short' });
}

/** « 31/08 – 06/09/2026 » */
export function formatWeekRange(monday: LocalDate): string {
  const sunday = addDays(monday, 6);
  return `${monday.slice(8, 10)}/${monday.slice(5, 7)} – ${formatFrenchDate(sunday)}`;
}

export const WEEKDAY_LABELS = [
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
] as const;

/** Monday first, as the week grid and the series form present them. */
export const WEEKDAY_OPTIONS = [1, 2, 3, 4, 5, 6, 0].map((weekday) => ({
  weekday,
  label: WEEKDAY_LABELS[weekday] ?? '',
}));

export function formatTimeRange(startsAt: LocalDateTime, durationMin: number): string {
  return `${timeOf(startsAt)} – ${timeOf(endOf(startsAt, durationMin))}`;
}
